/**
 * Central query keys for React Query cache management.
 * Ported from dashboard/lib/queries.ts.
 */
export const queries = {
  auth: {
    all: ['auth'] as const,
    currentUser: () => [...queries.auth.all, 'current-user'] as const,
  },
  devices: {
    all: ['devices'] as const,
    lists: () => [...queries.devices.all, 'list'] as const,
    list: () => [...queries.devices.lists()] as const,
    details: () => [...queries.devices.all, 'detail'] as const,
    detail: (id: string) => [...queries.devices.details(), id] as const,
    policies: (deviceId: string) => [...queries.devices.detail(deviceId), 'policies'] as const,
    status: (deviceId: string) => [...queries.devices.detail(deviceId), 'status'] as const,
  },
  helper: {
    all: ['helper'] as const,
    status: () => [...queries.helper.all, 'status'] as const,
  },
  schedules: {
    all: ['schedules'] as const,
    list: () => [...queries.schedules.all, 'list'] as const,
    detail: (id: string) => [...queries.schedules.all, 'detail', id] as const,
  },
  rules: {
    all: ['rules'] as const,
    list: () => [...queries.rules.all, 'list'] as const,
    detail: (id: string) => [...queries.rules.all, 'detail', id] as const,
  },
  policies: {
    all: ['policies'] as const,
    list: () => [...queries.policies.all, 'list'] as const,
    detail: (id: string) => [...queries.policies.all, 'detail', id] as const,
    protectedBundleIds: () => [...queries.policies.all, 'protected-bundle-ids'] as const,
    protectedApps: () => [...queries.policies.all, 'protected-apps'] as const,
  },
  urlSuggestions: {
    all: ['url-suggestions'] as const,
    search: (query: string) => [...queries.urlSuggestions.all, query] as const,
  },
  appUrls: {
    all: ['app-urls'] as const,
    forBundleIds: (bundleIds: string[]) => [...queries.appUrls.all, bundleIds] as const,
  },
  students: {
    all: ['students'] as const,
    list: () => [...queries.students.all, 'list'] as const,
    detail: (id: string) => [...queries.students.all, 'detail', id] as const,
  },
  admin: {
    all: ['admin'] as const,
    users: (params?: Record<string, unknown>) => [...queries.admin.all, 'users', params] as const,
    promoCodes: () => [...queries.admin.all, 'promo-codes'] as const,
    systemRules: () => [...queries.admin.all, 'system-rules'] as const,
    systemRule: (id: string) => [...queries.admin.all, 'system-rules', id] as const,
    protectedApps: () => [...queries.admin.all, 'protected-apps'] as const,
    userChoiceOptions: () => [...queries.admin.all, 'user-choice-options'] as const,
    policyMetrics: () => [...queries.admin.all, 'policy-metrics'] as const,
    schools: () => [...queries.admin.all, 'schools'] as const,
    schoolStaff: (schoolId: string) => [...queries.admin.all, 'schools', schoolId, 'staff'] as const,
    auditLogs: (filters?: Record<string, unknown>) => [...queries.admin.all, 'audit-logs', filters] as const,
  },
  school: {
    all: ['school'] as const,
    roster: (schoolId: string, filters?: Record<string, unknown>) =>
      filters
        ? ([...queries.school.all, schoolId, 'roster', filters] as const)
        : ([...queries.school.all, schoolId, 'roster'] as const),
    groups: (schoolId: string) =>
      [...queries.school.all, schoolId, 'groups'] as const,
    groupMembers: (schoolId: string, groupId: string) =>
      [...queries.school.all, schoolId, 'groups', groupId, 'members'] as const,
    policies: (schoolId: string) =>
      [...queries.school.all, schoolId, 'policies'] as const,
    policy: (schoolId: string, policyId: string) =>
      [...queries.school.all, schoolId, 'policies', policyId] as const,
    schedules: (schoolId: string) =>
      [...queries.school.all, schoolId, 'schedules'] as const,
    staff: (schoolId: string) =>
      [...queries.school.all, schoolId, 'staff'] as const,
    info: (schoolId: string) =>
      [...queries.school.all, schoolId, 'info'] as const,
    billing: (schoolId: string) =>
      [...queries.school.all, schoolId, 'billing'] as const,
    seats: (schoolId: string) =>
      [...queries.school.all, schoolId, 'seats'] as const,
    detectedDevices: (schoolId: string) =>
      [...queries.school.all, schoolId, 'detected-devices'] as const,
    helperCommands: (schoolId: string) =>
      [...queries.school.all, schoolId, 'helper-commands'] as const,
    helperRoster: (schoolId: string, filters?: Record<string, unknown>) =>
      filters
        ? ([...queries.school.all, schoolId, 'helper-roster', filters] as const)
        : ([...queries.school.all, schoolId, 'helper-roster'] as const),
  },
}
